import React from "react";

// Base interface for common image properties
interface BaseImageProps {
  className?: string;
}

// Interface for the hero image component
interface HeroImageProps extends BaseImageProps {
  imageUrl: string;
  alt: string;
}

// Interface for the gallery image component
interface GalleryImageProps extends HeroImageProps {
  onClick?: (event: React.MouseEvent<HTMLImageElement>) => void;
  onLoad?: (event: React.SyntheticEvent<HTMLImageElement>) => void;
}

// Basic skeleton component
const ImageSkeleton: React.FC<BaseImageProps> = ({ className = "" }) => {
  return (
    <div className={`animate-pulse bg-gray-200 ${className}`}>
      <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200" />
    </div>
  );
};

// Hero image component with skeleton loading
export const HeroImageWithSkeleton: React.FC<HeroImageProps> = ({
  imageUrl,
  alt,
  className = "",
}) => {
  const [isLoading, setIsLoading] = React.useState(true);

  return (
    <div className={`relative ${className}`}>
      {isLoading && <ImageSkeleton className="absolute inset-0" />}
      <img
        src={imageUrl}
        alt={alt}
        className={`w-full h-full object-cover ${
          isLoading ? "opacity-0" : "opacity-100"
        } transition-opacity duration-300`}
        onLoad={() => setIsLoading(false)}
      />
    </div>
  );
};

// Gallery image component with skeleton loading
export const GalleryImageWithSkeleton: React.FC<GalleryImageProps> = ({
  imageUrl,
  alt,
  onClick,
  onLoad,
  className = "",
}) => {
  const [isLoading, setIsLoading] = React.useState(true);

  const handleLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    setIsLoading(false);
    onLoad?.(event); // Call the optional onLoad handler if provided
  };

  return (
    <div
      className={`relative overflow-hidden group cursor-pointer ${className}`}
    >
      {isLoading && <ImageSkeleton className="absolute inset-0" />}
      <img
        src={imageUrl}
        alt={alt}
        className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 ${
          isLoading ? "opacity-0" : "opacity-100"
        } transition-opacity duration-300`}
        onLoad={handleLoad}
        onClick={onClick}
      />
    </div>
  );
};

export default ImageSkeleton;
